/**
 * Step 7: Calorie Goal - Display calculated goals with protein/kg and fat/kg adjustment
 */
import React from 'react';
import { CYCLE_TYPES } from './StepFitnessGoal';

const StepCalorieGoal = ({ prefs, calculatedGoals, bodyComposition, setPrefs }) => {
  const selectedCycle = CYCLE_TYPES.find(c => c.id === prefs.cycle_type);
  const proteinPerKg = prefs.protein_per_kg || selectedCycle?.protein_per_kg || 1.6;
  const fatPerKg = prefs.fat_per_kg || calculatedGoals?.fat_per_kg || 0.8;
  
  return (
    <div className="space-y-4" data-testid="onboarding-step-calorie-goal">
      <div className="text-center mb-6">
        <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-gradient-to-br from-orange-500 to-red-500 flex items-center justify-center">
          <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2c-4 0-8 4-8 8 0 3 2 6 8 12 6-6 8-9 8-12 0-4-4-8-8-8z"/>
          </svg>
        </div>
        <h3 className="text-xl font-bold text-white">Dein persönliches Ziel</h3>
        <p className="text-zinc-400 text-sm">Basierend auf deinen Angaben</p>
      </div>
      
      {calculatedGoals && (
        <>
          {/* Cycle Badge */}
          <div className="text-center">
            <span className="inline-flex items-center gap-2 text-lg px-4 py-2 rounded-full bg-amber-500/20 text-amber-400 border border-amber-500/30">
              {selectedCycle?.icon && <selectedCycle.icon className="w-5 h-5 shrink-0" />}
              {selectedCycle?.label} ({selectedCycle?.adjustment > 0 ? '+' : ''}{selectedCycle?.adjustment}%)
            </span>
          </div>
          
          {/* Main Calorie Display */}
          <div className="p-6 bg-gradient-to-br from-orange-500/10 to-red-500/10 rounded-2xl border border-orange-500/30 text-center">
            <p className="text-sm text-zinc-400 mb-1">Tägliches Kalorienziel</p>
            <div className="text-5xl font-bold text-orange-400">
              {calculatedGoals.daily_calories}
              <span className="text-xl font-normal text-zinc-400 ml-2">kcal</span>
            </div>
          </div>
          
          {/* Calculation Breakdown */}
          <div className="p-4 bg-zinc-800/50 rounded-xl space-y-2 text-sm">
            <div className="flex justify-between text-zinc-400">
              <span>Grundumsatz (BMR)</span>
              <span className="text-white font-medium">{calculatedGoals.bmr} kcal</span>
            </div>
            <div className="flex justify-between text-zinc-400">
              <span>+ Aktivität ({calculatedGoals.activity_description || 'TDEE'})</span>
              <span className="text-white font-medium">{calculatedGoals.tdee_with_work || calculatedGoals.tdee} kcal</span>
            </div>
            {calculatedGoals.work_bonus > 0 && (
              <div className="text-xs text-amber-400 italic ml-2">
                davon Arbeitsalltag: +{calculatedGoals.work_bonus} kcal
              </div>
            )}
            {prefs.activity_variant === 'daily' && (
              <div className="text-xs text-blue-400 italic">
                Täglich-Modus: Trainings werden separat erfasst
              </div>
            )}
            
            {/* Body Composition Adjustment */}
            {calculatedGoals.body_comp_info && (
              <div className="flex justify-between text-zinc-400">
                <span className="flex items-center gap-1">
                  <span>Körperzusammensetzung</span>
                  <span className="text-xs text-zinc-500">({calculatedGoals.body_comp_info.body_fat_percent}% KF)</span>
                </span>
                <span className={`font-medium ${calculatedGoals.body_comp_adjustment > 0 ? 'text-yellow-400' : calculatedGoals.body_comp_adjustment < 0 ? 'text-red-400' : 'text-white'}`}>
                  {calculatedGoals.body_comp_adjustment > 0 ? '+' : ''}{calculatedGoals.body_comp_adjustment} kcal
                </span>
              </div>
            )}
            {calculatedGoals.body_comp_info && (
              <div className="text-xs text-purple-400 italic">
                {calculatedGoals.body_comp_info.category_info?.description || calculatedGoals.body_comp_info.category} 
                {calculatedGoals.body_comp_info.data_source === 'inbody' && ' (InBody)'}
              </div>
            )}
            
            <div className="flex justify-between border-t border-zinc-700 pt-2 text-zinc-400">
              <span>{selectedCycle?.adjustment > 0 ? '+' : ''}{selectedCycle?.adjustment}% Zyklus-Anpassung</span>
              <span className="text-white font-medium">
                {selectedCycle?.adjustment > 0 ? '+' : ''}{Math.round((calculatedGoals.tdee_with_body_comp || calculatedGoals.tdee_with_work || calculatedGoals.tdee) * (selectedCycle?.adjustment || 0) / 100)} kcal
              </span>
            </div>
          </div>
          
          {/* No Body Composition Data Info */}
          {!calculatedGoals.body_comp_info && bodyComposition?.body_composition_enabled && (
            <div className="p-3 bg-purple-500/10 rounded-lg border border-purple-500/30">
              <p className="text-xs text-purple-400">
                <strong>Tipp:</strong> Trage deine Körperzusammensetzung (Körperfett %) im Körper-Tracking ein, 
                um eine noch genauere Berechnung zu erhalten.
              </p>
            </div>
          )}

          {/* Protein per kg Slider */}
          {setPrefs && (
            <div className="p-4 bg-zinc-800/50 rounded-xl border border-zinc-700" data-testid="protein-per-kg-slider">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-zinc-300">Protein pro kg Körpergewicht</span>
                <span className="text-lg font-bold text-blue-400">{proteinPerKg.toFixed(1)} g/kg</span>
              </div>
              <input
                type="range"
                min="1.0"
                max="3.0"
                step="0.1"
                value={proteinPerKg}
                onChange={(e) => setPrefs(prev => ({ ...prev, protein_per_kg: parseFloat(e.target.value) }))}
                className="w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-blue-500"
                data-testid="protein-per-kg-input"
              />
              <div className="flex justify-between text-xs text-zinc-500 mt-1">
                <span>1.0 g/kg</span>
                <span>2.0 g/kg</span>
                <span>3.0 g/kg</span>
              </div>
              <p className="text-xs text-zinc-500 mt-2">
                {parseFloat(prefs.weight_kg || 70)} kg x {proteinPerKg.toFixed(1)} g = <span className="text-blue-400 font-medium">{calculatedGoals.protein_grams}g Protein</span>
              </p>
            </div>
          )}

          {/* Fat per kg Slider */}
          {setPrefs && (
            <div className="p-4 bg-zinc-800/50 rounded-xl border border-zinc-700" data-testid="fat-per-kg-slider">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-zinc-300">Fett pro kg Körpergewicht</span>
                <span className="text-lg font-bold text-red-400">{fatPerKg.toFixed(1)} g/kg</span>
              </div>
              <input
                type="range"
                min="0.3"
                max="2.0"
                step="0.1"
                value={fatPerKg}
                onChange={(e) => setPrefs(prev => ({ ...prev, fat_per_kg: parseFloat(e.target.value) }))}
                className="w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-red-500"
                data-testid="fat-per-kg-input"
              />
              <div className="flex justify-between text-xs text-zinc-500 mt-1">
                <span>0.3 g/kg</span>
                <span>1.0 g/kg</span>
                <span>2.0 g/kg</span>
              </div>
              <p className="text-xs text-zinc-500 mt-2">
                {parseFloat(prefs.weight_kg || 70)} kg x {fatPerKg.toFixed(1)} g = <span className="text-red-400 font-medium">{calculatedGoals.fat_grams}g Fett</span>
              </p>
            </div>
          )}

          {/* Carbs Info */}
          {setPrefs && (
            <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/20">
              <p className="text-xs text-amber-400">
                Kohlenhydrate = Rest der Kalorien nach Protein + Fett = <span className="font-bold">{calculatedGoals.carbs_grams}g KH</span>
              </p>
            </div>
          )}
          
          {/* Macros */}
          <div className="grid grid-cols-3 gap-3">
            <div className="p-3 bg-blue-500/10 rounded-xl text-center border border-blue-500/20">
              <div className="text-2xl font-bold text-blue-400">{calculatedGoals.protein_grams}g</div>
              <div className="text-xs text-blue-300">Protein ({calculatedGoals.protein_percent}%)</div>
            </div>
            <div className="p-3 bg-amber-500/10 rounded-xl text-center border border-amber-500/20">
              <div className="text-2xl font-bold text-amber-400">{calculatedGoals.carbs_grams}g</div>
              <div className="text-xs text-amber-300">KH ({calculatedGoals.carbs_percent}%)</div>
            </div>
            <div className="p-3 bg-red-500/10 rounded-xl text-center border border-red-500/20">
              <div className="text-2xl font-bold text-red-400">{calculatedGoals.fat_grams}g</div>
              <div className="text-xs text-red-300">Fett ({calculatedGoals.fat_percent}%)</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default StepCalorieGoal;
